// Interactive SEER Reference Chart
// Shows annual operating cost for a standard 3-ton unit across SEER 10–24
// at a default $0.15/kWh rate and 1,500 annual hours

const BTU_HR = 36000 // 3 tons

interface ChartPoint {
  seer: number
  label: string
  annualCost: number
  badge?: string
}

function buildChartData(rate: number, hours: number): ChartPoint[] {
  const seers = [10, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 24]
  return seers.map((seer) => {
    const kw = BTU_HR / (seer * 1000)
    const cost = kw * hours * rate
    let badge: string | undefined
    if (seer === 13) badge = 'Min Legal'
    if (seer === 16) badge = 'Standard'
    if (seer === 18) badge = 'Good'
    if (seer === 20) badge = 'High Eff.'
    if (seer === 24) badge = 'Elite'
    return { seer, label: `SEER ${seer}`, annualCost: cost, badge }
  })
}

export default function SEERReferenceChart() {
  const rate = 0.15
  const hours = 1500

  const data = buildChartData(rate, hours)
  const maxCost = Math.max(...data.map((d) => d.annualCost))
  const baseline = data[0].annualCost // SEER 10

  return (
    <div
      className="rounded-2xl border border-slate-700/50 bg-slate-900/70 p-6 lg:p-8 overflow-hidden"
      role="region"
      aria-label="SEER rating operating cost reference chart for a 3-ton air conditioner"
    >
      {/* Header info */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">
            Reference Conditions
          </p>
          <p className="text-sm text-slate-400">
            3-ton (36,000 BTU) unit &bull; 1,500 hrs/yr &bull; $0.15/kWh
          </p>
        </div>
        <div className="flex gap-3 text-xs">
          <span className="px-2.5 py-1 rounded-full bg-red-900/30 border border-red-700/30 text-red-400">
            High Cost
          </span>
          <span className="px-2.5 py-1 rounded-full bg-green-900/30 border border-green-700/30 text-green-400">
            Low Cost
          </span>
        </div>
      </div>

      {/* Bar chart */}
      <div
        className="space-y-2.5 overflow-x-auto"
        role="list"
        aria-label="Annual operating cost by SEER rating"
      >
        {data.map((point) => {
          const barPct = (point.annualCost / maxCost) * 100
          const savingVsBaseline = ((baseline - point.annualCost) / baseline) * 100
          // Color gradient: red (low SEER) → yellow → green (high SEER)
          const hue = Math.round(
            ((point.seer - 10) / (24 - 10)) * 120 // 0=red, 120=green
          )
          const barColor = `hsl(${hue}, 75%, 42%)`
          const barColorLight = `hsl(${hue}, 75%, 55%)`

          return (
            <div
              key={point.seer}
              role="listitem"
              className="flex items-center gap-3 group"
            >
              {/* SEER label */}
              <div className="w-16 flex-shrink-0 flex flex-col items-end">
                <span className="text-xs font-bold text-slate-300 tabular-nums">
                  SEER {point.seer}
                </span>
                {point.badge && (
                  <span
                    className="text-[9px] text-slate-500 whitespace-nowrap"
                    aria-label={`${point.badge} tier`}
                  >
                    {point.badge}
                  </span>
                )}
              </div>

              {/* Bar */}
              <div className="flex-1 h-8 bg-slate-800/60 rounded-lg overflow-hidden relative">
                <div
                  className="h-full rounded-lg transition-all duration-700 ease-out flex items-center justify-end pr-3"
                  style={{
                    width: `${barPct}%`,
                    background: `linear-gradient(to right, ${barColor}, ${barColorLight})`,
                  }}
                  aria-hidden="true"
                >
                  <span className="text-xs font-bold text-white/90 tabular-nums whitespace-nowrap">
                    ${point.annualCost.toFixed(0)}/yr
                  </span>
                </div>
              </div>

              {/* Savings vs SEER 10 */}
              <div
                className="w-20 flex-shrink-0 text-right"
                aria-label={`${savingVsBaseline.toFixed(0)}% less than SEER 10`}
              >
                {savingVsBaseline > 0 ? (
                  <span className="text-xs font-semibold text-green-400 tabular-nums">
                    −{savingVsBaseline.toFixed(0)}%
                  </span>
                ) : (
                  <span className="text-xs text-slate-600">baseline</span>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Legend */}
      <div
        className="mt-6 pt-4 border-t border-slate-700/40 flex flex-wrap gap-4 text-xs text-slate-500"
        aria-label="Chart legend"
      >
        <span>
          <span aria-hidden="true" className="text-green-400 font-bold">−%</span> = savings vs. SEER 10 baseline
        </span>
        <span>Cost = kW × run hours × $/kWh</span>
        <span>1 ton = 12,000 BTU/hr</span>
      </div>
    </div>
  )
}
